
import { useCallback, useEffect, useMemo, useState } from "react";
import type { SpaceSoundtrack, SoundtrackAsset } from "@mind-sands/shared";
import { api } from "../api";
import { Select } from "@/components/ui/Select";
import { Slider } from "@/components/ui/Slider";
import { ChevronDown, Music, X } from "lucide-react";

/** Convert a track ID like "04-snow-ambient" into a readable label "Snow Ambient". */
function trackIdToLabel(trackId: string): string {
  return trackId
    .replace(/^\d+[-_]?/, "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format seconds as "m:ss", or null if not a finite number. */
function formatDuration(seconds: number | undefined): string | null {
  if (typeof seconds !== "number" || !Number.isFinite(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Format bytes as a human-readable file size, or null if not a finite number. */
function formatFileSize(bytes: number | undefined): string | null {
  if (typeof bytes !== "number" || !Number.isFinite(bytes)) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

interface SoundtrackEditorProps {
  soundtracks?: SpaceSoundtrack[];
  onChange: (soundtracks: SpaceSoundtrack[] | undefined) => void;
}

/**
 * Soundtrack assignment editor.
 * Loads the available track catalog, renders assigned tracks with volume/remove,
 * and provides an "Add track" dropdown.
 */
export function SoundtrackEditor({
  soundtracks,
  onChange,
}: SoundtrackEditorProps) {
  const [availableTracks, setAvailableTracks] = useState<SoundtrackAsset[]>([]);
  const [tracksLoaded, setTracksLoaded] = useState(false);

  // Load available soundtracks on mount
  useEffect(() => {
    if (tracksLoaded) return;
    api
      .listSoundtracks()
      .then((tracks) => {
        setAvailableTracks(tracks);
        setTracksLoaded(true);
      })
      .catch(() => {
        // Silently fail — soundtrack catalog is optional
        setTracksLoaded(true);
      });
  }, [tracksLoaded]);

  const currentTracks = useMemo(() => soundtracks ?? [], [soundtracks]);
  const hasTracks = currentTracks.length > 0;
  const [open, setOpen] = useState(hasTracks);

  useEffect(() => {
    setOpen(hasTracks);
  }, [hasTracks]);

  /** Catalog lookup by track ID — provides duration/fileSize metadata. */
  const trackAssetMap = useMemo(
    () => new Map(availableTracks.map((t) => [t.id, t])),
    [availableTracks],
  );

  const handleAdd = useCallback(
    (trackId: string) => {
      const newTrack: SpaceSoundtrack = {
        trackId,
        volume: 0.5,
        loop: true,
        fadeIn: 3,
      };
      onChange([...currentTracks, newTrack]);
    },
    [currentTracks, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const updated = currentTracks.filter((_, i) => i !== index);
      onChange(updated.length > 0 ? updated : undefined);
    },
    [currentTracks, onChange],
  );

  const handleVolume = useCallback(
    (index: number, volume: number) => {
      const updated = currentTracks.map((t, i) =>
        i === index ? { ...t, volume } : t,
      );
      onChange(updated);
    },
    [currentTracks, onChange],
  );

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="mb-1 flex w-full items-center gap-1 px-1 text-[10px] font-medium uppercase tracking-wider text-sands-400 hover:text-sands-600"
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`}
        />
        Soundtracks
        {hasTracks && (
          <span className="normal-case tracking-normal text-sands-400">
            ({currentTracks.length})
          </span>
        )}
      </button>

      {open && (
        <>
          {!hasTracks && (
            <p className="px-2 pb-1 text-[11px] text-sands-400">
              No soundtracks assigned
            </p>
          )}

          {currentTracks.map((track, index) => {
            const asset = trackAssetMap.get(track.trackId);
            const meta = [
              formatDuration(asset?.duration),
              formatFileSize(asset?.fileSize),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div
                key={`${track.trackId}-${index}`}
                className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded-lg bg-sands-50 border border-sands-100"
              >
                <Music className="w-3.5 h-3.5 text-healing-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-sands-700 truncate">
                    {trackIdToLabel(track.trackId)}
                  </p>
                  {meta && (
                    <p className="text-[10px] text-sands-400 truncate">{meta}</p>
                  )}
                </div>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={track.volume ?? 0.5}
                  onChange={(e) =>
                    handleVolume(index, parseFloat(e.target.value))
                  }
                  size="compact"
                  title={`Volume: ${Math.round((track.volume ?? 0.5) * 100)}%`}
                />
                <button
                  onClick={() => handleRemove(index)}
                  className="w-5 h-5 flex items-center justify-center rounded text-sands-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                  title="Remove track"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}

          <div className="px-1 mt-1">
            <Select
              variant="dashed"
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  handleAdd(e.target.value);
                  e.target.value = "";
                }
              }}
            >
              <option value="" disabled>
                {availableTracks.length > 0
                  ? "+ Add a soundtrack..."
                  : "Loading soundtracks..."}
              </option>
              {availableTracks
                .filter((t) => !currentTracks.some((s) => s.trackId === t.id))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
          </div>
        </>
      )}
    </div>
  );
}
